import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select } from '@/components/ui/select';

import { getCatalogPresetsOptions } from './api';

export interface ProjectCatalogPresetSelection {
  full?: string;
  limited?: string;
}

export function ProjectCatalogPresets({
  tenantId,
  value,
  onChange,
}: {
  tenantId: string;
  value: ProjectCatalogPresetSelection;
  onChange: (value: ProjectCatalogPresetSelection) => void;
}) {
  const { t } = useTranslation();
  const presets = useQuery(getCatalogPresetsOptions(tenantId));
  const [bothModes, setBothModes] = useState(false);
  // The parent stores this value directly; only external replacements reset the checkbox.
  const expectedValueRef = useRef(value);
  const { error, errorUpdatedAt } = presets;

  useEffect(() => {
    if (value !== expectedValueRef.current) setBothModes(false);
    expectedValueRef.current = value;
  }, [value]);

  const changeValue = (nextValue: ProjectCatalogPresetSelection) => {
    expectedValueRef.current = nextValue;
    onChange(nextValue);
  };

  useEffect(() => {
    const toastId = `project-catalog-presets-load-${tenantId}`;
    if (!error || !errorUpdatedAt) {
      toast.dismiss(toastId);
      return;
    }
    toast.error(t('catalog.presetLoadError'), { toastId });
  }, [error, errorUpdatedAt, tenantId, t]);
  const options =
    presets.data?.presets.map((preset) => ({ id: preset.id, title: preset.name })) ?? [];

  return (
    <section className="rounded-md border border-background-lighter">
      <h2 className="rounded-t-md bg-background-lighter px-4 py-2 sm:px-10">
        {t('catalog.title')}
      </h2>
      <div className="flex flex-col gap-3 px-4 py-5 sm:px-10">
        <p className="text-sm text-text-secondary">{t('catalog.creationDescription')}</p>
        <Checkbox
          label={t('catalog.creationBothModes')}
          checked={bothModes}
          onCheckedChange={(checked) => {
            const enabled = checked === true;
            setBothModes(enabled);
            if (enabled) {
              const sharedPreset = value.full ?? value.limited;
              changeValue({ full: sharedPreset, limited: sharedPreset });
            }
          }}
        />
        {presets.error && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            disabled={presets.isFetching}
            onClick={() => void presets.refetch()}
          >
            {t('catalog.retry')}
          </Button>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('catalog.full')}
            size="sm"
            placeholder={t('catalog.noPreset')}
            value={value.full}
            options={options}
            disabled={presets.isPending}
            onChange={({ target }) =>
              changeValue({
                ...value,
                full: target.value || undefined,
                ...(bothModes ? { limited: target.value || undefined } : {}),
              })
            }
          />
          <Select
            label={t('catalog.limited')}
            size="sm"
            placeholder={t('catalog.noPreset')}
            value={value.limited}
            options={options}
            disabled={presets.isPending || bothModes}
            onChange={({ target }) => changeValue({ ...value, limited: target.value || undefined })}
          />
        </div>
      </div>
    </section>
  );
}
